/**
 * CommunitiesTabContent Component
 * Content for the Communities tab in the profile detail view
 * Shows community cards the user belongs to, with search, filters, and infinite scroll
 */

import { Search, X } from "lucide-react"
import { useEffect, useState } from "react"
import { CommunityCard } from "@/components/cards/communityCard"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { LoginModal } from "@/features/auth/components/LoginModal"
import { useUserCommunities } from "@/features/communities/hooks/useUserCommunities"
import { TabEmptyState } from "@/features/my-rituality/components/TabEmptyState"
import { useInfiniteScroll } from "@/hooks/useInfiniteScroll"
import { useTranslation } from "@/language"
import { cn } from "@/lib"

type FilterType = "all" | "created" | "joined"
type SortType = "newest" | "oldest"

interface CommunitiesTabContentProps {
  totalCount?: number
  className?: string
  userId?: string
  searchQuery?: string
  showEnhancedEmpty?: boolean
  isGuest?: boolean
}

export function CommunitiesTabContent({
  className,
  userId,
  searchQuery,
  showEnhancedEmpty = false,
  isGuest = false,
}: CommunitiesTabContentProps) {
  const { t } = useTranslation("profile")

  // Search state with debounce
  const [debouncedSearch, setDebouncedSearch] = useState("")

  const [showSearch, setShowSearch] = useState(false)
  const [localSearch, setLocalSearch] = useState("")
  const [showLoginModal, setShowLoginModal] = useState(false)

  // Filter state
  const [filter, setFilter] = useState<FilterType>("all")
  const [sort, setSort] = useState<SortType>("newest")

  // Debounce search (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(localSearch || searchQuery || "")
    }, 300)
    return () => clearTimeout(timer)
  }, [localSearch, searchQuery])

  // Active filters for badge display
  const activeFilters: Array<{ key: string; label: string; onClear: () => void }> = []

  if (filter !== "all") {
    activeFilters.push({
      key: "type",
      label: filter === "created" ? t("communitiesTab.created") : t("communitiesTab.joined"),
      onClear: () => setFilter("all"),
    })
  }

  if (sort !== "newest") {
    activeFilters.push({
      key: "sort",
      label: sort === "oldest" ? t("communitiesTab.oldest") : t("communitiesTab.newest"),
      onClear: () => setSort("newest"),
    })
  }

  // Infinite scroll hook
  const { data, isLoading, isError, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useUserCommunities(userId || "", {
      search: debouncedSearch,
      filter,
      sort,
    })

  // Flatten all pages
  const communities = data?.pages.flatMap(page => page.items) ?? []

  // Load more callback
  const onLoadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage()
    }
  }

  // Infinite scroll sentinel ref
  const loadMoreRef = useInfiniteScroll({
    onLoadMore,
    hasMore: hasNextPage ?? false,
    threshold: 200,
  })

  const hasActiveFilters = localSearch || searchQuery || filter !== "all" || sort !== "newest"

  return (
    <div className={cn("md:space-y-2 px-5 md:px-0", className)}>
      <div className="flex items-center justify-between">
        <div className="flex md:items-center flex-col w-full md:flex-row gap-1.25 md:gap-3.75">
          <div className="flex justify-between w-full items-center gap-2">
            <div className="text-[18px] font-semibold leading-7.5 uppercase text-text-primary">
              Communities
            </div>
            <div className="lg:hidden flex">
              {showSearch ? (
                <div className="flex items-center gap-2 rounded-sm border border-white/15 bg-white/5 px-3 py-1.5 h-7.5">
                  <Search className="w-4 h-4 text-primary/50 shrink-0" />
                  <Input
                    type="text"
                    placeholder="Search..."
                    value={localSearch}
                    onChange={e => setLocalSearch(e.target.value)}
                    className="max-w-48 border-none h-7.5 bg-transparent px-2 text-sm text-primary shadow-none focus-visible:ring-0"
                    autoFocus
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="shrink-0 h-6 w-6 text-primary/50"
                    onClick={() => {
                      setShowSearch(false)
                      setLocalSearch("")
                    }}
                    aria-label="Close search"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <button
                  type="button"
                  className="opacity-50 cursor-pointer h-5"
                  onClick={() => (isGuest ? setShowLoginModal(true) : setShowSearch(true))}
                  aria-label="Search"
                >
                  <Search className="w-5 h-5 text-primary" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      <p className="mt-1.25 md:mt-3 text-[14px] leading-[25px]">
        {t("communitiesTab.description")}
      </p>

      {/* Loading State */}
      {isLoading && (
        <div className="text-center py-8 text-stone">{t("communitiesTab.loading")}</div>
      )}

      {/* Error State */}
      {isError && <div className="text-center py-8 text-red">{t("communitiesTab.error")}</div>}

      {/* Empty State */}
      {!isLoading &&
        !isError &&
        communities.length === 0 &&
        (showEnhancedEmpty && !hasActiveFilters ? (
          <TabEmptyState
            title={t("communitiesTab.emptyTitle")}
            description={t("communitiesTab.emptyDescription")}
            ctaLabel={t("communitiesTab.exploreCommunities")}
            ctaTo="/connect"
          />
        ) : (
          <div className="text-center py-8 text-stone">
            {hasActiveFilters ? t("communitiesTab.noResults") : t("communitiesTab.empty")}
          </div>
        ))}

      {/* Community Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 my-7.5 md:my-10">
        {communities.map(community => (
          <CommunityCard key={community.id} community={community} />
        ))}
      </div>

      {/* Infinite Scroll Sentinel */}
      {(hasNextPage || isFetchingNextPage) && (
        <div ref={loadMoreRef} className="h-4" aria-hidden="true" />
      )}

      {/* Loading More Indicator */}
      {isFetchingNextPage && (
        <div className="text-center py-4 text-stone text-sm">{t("communitiesTab.loadingMore")}</div>
      )}
      <LoginModal open={showLoginModal} onOpenChange={setShowLoginModal} />
    </div>
  )
}
